import { Badge, Button, Modal } from '@safetyhub/ui';
import { isAssignmentSourceAllowedAtSlot, PARKED_SLOT_SIGNER_NAME } from '@safetyhub/shared';
import type { ApprovalLineItem } from '@safetyhub/report-composer';
import { OrganizationMember } from '../../../../../lib/organizations';
import { isOrganizationMember } from '../../../../../lib/members';
import { EditModalFooter } from '../../../../common/EditModalFooter';
import {
  IDENTITY_LOST_NAME_SUFFIX,
  PARKED_FILL_ROW_WARNING,
  UNRESOLVED_NAME_SUFFIX,
  UNRESOLVED_ROW_WARNING,
  isDelegationOwnedStep,
  isIdentityLostApprovalStep,
  isUnresolvedApprovalStep,
} from '../approvalLineIdentity';
import type { ApprovalLineItemWithWhitelist } from '../hooks/useApprovalLineEdit';
import { defaultNotifyVia, selectedNotifyKinds } from '../notifyChannelOptions';
import type { NotifyKind } from '../notifyChannelOptions';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  DragHandleIcon,
  UserMinusIcon,
} from './icons';
// 잠긴 칸 표식은 공용 `components/icons`의 `LockIcon` — 권한 매트릭스의 잠긴 키와 같은 뜻·같은 path다.
import { LockIcon } from '../../../../icons';

function formatPhone(phone: string): string {
  if (phone.length === 11) return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
  if (phone.length === 10) return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
  return phone;
}

/**
 * 조직 내부 멤버 여부 판별 — 구성원 자격(`countsAsMember`, 서버 판정)이면 내부 조직 멤버, 아니면 외부(현장·업체) 담당자.
 * 서버 createExternalMember는 배정 0(구성원 아님)으로 만들므로 `isOrganizationMember`(lib/members)가 SSOT(P7-b-2 — 옛 `role != null`).
 */
function isInternalMember(orgMember: OrganizationMember): boolean {
  return isOrganizationMember(orgMember);
}

/**
 * 통지가 **도달할 경로가 하나도 없는가**(F-F).
 *
 * 경계는 계정 유무다. 계정이 있으면 연락처가 없어도 인앱 결재 요청이 간다 —
 * prod 김안전이 그 상태로 결재 7건을 실제로 처리했다. 계정이 없으면 인앱이 원천 불가라
 * 연락처가 유일한 도달 수단이고, 그마저 없으면 지정 자체를 막는다(서버도 같은 규칙으로 400).
 *
 * ⚠️ "로그인한 적 없음"은 쓰지 않는다 — 로그인 기록 적재가 2026-08부터라 그 이전 로그인이
 * 보이지 않는다. 대신 경고 문구가 *로그인하지 않으면 인앱을 못 본다*는 사실만 말한다.
 */
function isUnreachableMember(orgMember: OrganizationMember): boolean {
  const hasAccount = !!(orgMember.userId ?? orgMember.user?.id);
  const phone = orgMember.member?.phone ?? orgMember.user?.phone;
  const email = orgMember.member?.email;
  const accountEmail = orgMember.user?.auths?.find((a) => a.provider === 'EMAIL')?.providerId;
  return !hasAccount && !phone && !email && !accountEmail;
}

/** OrganizationMember에서 picker 표시용 라벨 생성 */
function getMemberLabel(orgMember: OrganizationMember): string {
  const name = orgMember.member?.name ?? orgMember.user?.name ?? '-';
  const phone = orgMember.member?.phone ?? orgMember.user?.phone;
  const email = orgMember.member?.email;
  const accountEmail = orgMember.user?.auths?.find((a) => a.provider === 'EMAIL')?.providerId;
  const contactHint = [phone ? 'SMS' : '', email || accountEmail ? 'Email' : '']
    .filter(Boolean)
    .join('+');
  // 도달 불가는 힌트가 아니라 **경고**로 보여야 고르기 전에 알아챈다
  if (isUnreachableMember(orgMember)) return `${name} · 연락처 없음 — 선택하면 입력`;
  return `${name}${contactHint ? ` · ${contactHint}` : ''}`;
}

/** OrganizationMember에서 고유 memberId 추출 */
function getMemberId(orgMember: OrganizationMember): string {
  return orgMember.member?.id ?? orgMember.memberId ?? orgMember.id;
}

/** 담당자 미지정 칸이 무엇을 뜻하는지 — 배지/안내에서 같은 문구를 쓰도록 한곳에 둔다. */
const UNASSIGNED_BADGE_LABEL = '담당자 미지정';
const UNASSIGNED_HINT = '이 칸은 종이(수기)로 결재합니다. 출력물에 빈 서명란으로 남습니다.';

/**
 * 결재자 후보 option 목록 — 칸별 담당자 지정 select와 칸 추가 select가 공유한다.
 * 두 곳이 갈리면 한쪽에서만 보이는 결재자가 생기므로 한 컴포넌트로 묶는다.
 */
function MemberOptions({ availableMembers }: { availableMembers: OrganizationMember[] }) {
  const internal = availableMembers.filter(isInternalMember);
  const external = availableMembers.filter((m) => !isInternalMember(m));
  return (
    <>
      {internal.length > 0 && (
        <optgroup label="조직 멤버">
          {internal.map((orgMember) => (
            <option
              key={getMemberId(orgMember)}
              value={getMemberId(orgMember)}
              // F-F/F-G: 도달 경로가 0인 사람도 **고를 수는 있다** — 고르는 순간 연락처 입력
              // 폼이 열리고, 채우면 그대로 지정된다. disabled로 두면 왜 못 고르는지는 보여도
              // 조치로 이어지지 않아 막다른 길이 된다(서버는 그대로 400으로 막는다).
            >
              {getMemberLabel(orgMember)}
            </option>
          ))}
        </optgroup>
      )}
      {external.length > 0 && (
        <optgroup label="현장·업체 담당자">
          {external.map((orgMember) => (
            <option
              key={getMemberId(orgMember)}
              value={getMemberId(orgMember)}
              // F-F/F-G: 도달 경로가 0인 사람도 **고를 수는 있다** — 고르는 순간 연락처 입력
              // 폼이 열리고, 채우면 그대로 지정된다. disabled로 두면 왜 못 고르는지는 보여도
              // 조치로 이어지지 않아 막다른 길이 된다(서버는 그대로 400으로 막는다).
            >
              {getMemberLabel(orgMember)}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

/**
 * 푸터 — 취소가 base Modal의 `requestClose`로 수렴해야 dirty 닫기 확인을 탄다
 * (`EditModalFooter`가 그 규약을 소유). context는 Modal children/footer **하위 컴포넌트**에서만
 * 잡히므로 분리한다(base Modal 규약 — 부모 본문에서 훅을 부르면 null).
 */
function ApprovalEditModalFooter({
  showReset,
  onReset,
  isSaving,
  isDirty,
  onSave,
  onFallbackClose,
}: {
  showReset: boolean;
  onReset?: () => void;
  isSaving: boolean;
  isDirty: boolean;
  onSave: () => void;
  onFallbackClose: () => void;
}) {
  return (
    <>
      {showReset && onReset && (
        <button
          type="button"
          onClick={onReset}
          disabled={isSaving}
          className="px-3 py-2 text-sm text-ink-500 hover:text-red-600 underline disabled:opacity-50"
        >
          {/* COMPOSED(템플릿 양식)도 되돌아가는 기준은 **현장 기본**이다 —
              되돌리기가 부르는 COMPOSED preview가 현장 오버라이드를 템플릿 위에 얹어
              돌려주기 때문(`ReportComposerService.resolveComposedApprovalMeta`).
              현장 오버라이드가 없을 때 템플릿 값이 나오는 것은 cascade의 폴백이지
              다른 기준이 아니므로, 라벨을 type별로 가를 이유가 없다. */}
          현장 기본으로 되돌리기
        </button>
      )}
      {/* 무변경이면 저장이 왜 비활성인지 보여준다(DirtyHint) — EditModalFooter 표준 골격 */}
      <EditModalFooter
        isDirty={isDirty}
        submitLabel="저장"
        submitDisabled={isSaving || !isDirty}
        busy={isSaving}
        onSubmit={onSave}
        onFallbackClose={onFallbackClose}
      />
    </>
  );
}

interface EditModalProps {
  /**
   * 오버레이 층(z-index Tailwind 클래스) — **여는 쪽이 주입**한다(모달 z 정책).
   *
   * 이 모달은 두 층에서 쓰인다: 상세 헤더의 '결재라인 편집'은 **root**(z-[70])지만,
   * 위임 모달(`ReportDelegationModal`, z-[80]) 안에서 열릴 때는 sub2(z-[90])여야 한다.
   * 종전에는 항상 z-[70]이라 위임 흐름에서 **위임 모달 뒤로 숨었다**(2026-08-11).
   * 같은 컴포넌트가 두 깊이에 놓이므로 자기 층을 계산하지 않는다 — 값은 `MODAL_LAYER`
   * 상수로, 여는 쪽(`ApprovalLineSectionImpl`)이 정한다. 미지정 시 base 기본(root z-[70]).
   */
  overlayZClassName?: string;
  /** 오버레이 배경 — 겹침 층(sub2)에서는 옅게(`SUB_MODAL_OVERLAY_BG`) 주입. 기본 bg-black/40. */
  overlayBgClassName?: string;
  editingLines: ApprovalLineItem[];
  /** 회사 멤버 목록 (중복 없음) */
  availableMembers: OrganizationMember[];
  isSaving: boolean;
  /**
   * 모달 오픈 이후 내용 변경 여부(`useApprovalLineEdit.isDirty`). false면 저장을 비활성한다 —
   * 무변경 저장이 현장 기본 결재선(`saveAsSiteDefault` 기본 true)까지 재기록해 가짜 변경
   * 이력을 쌓는 것을 입구에서 막는다. 미전달 시 true(종전 동작 유지).
   */
  isDirty?: boolean;
  draggedIndex: number | null;
  dragOverIndex: number | null;
  onClose: () => void;
  onSave: () => void;
  onAddDropdownChange: (value: string) => void;
  /**
   * **빈 결재란(담당자 미지정) 하나를 목록 끝에 추가**한다.
   * 칸 추가와 담당자 지정은 별개 축이라(결정 2026-08-13) 이쪽이 주 액션이고,
   * 담당자는 각 칸의 '담당자 지정...'에서 정한다.
   */
  onAddUnassignedSlot: () => void;
  onRemoveApprover: (index: number) => void;
  /**
   * 담당자 해제 — **칸은 남기고** 담당자만 뗀다(칸 제거는 `onRemoveApprover`).
   * locked 칸에도 허용된다: locked는 칸 구조에 대한 권한이지 담당자에 대한 것이 아니다
   * (결정 2026-08-13 §7).
   */
  onUnsetAssignee: (index: number) => void;
  /** 이미 있는 칸에 멤버를 담당자로 지정(칸 추가가 아니다 — 중간 칸을 채우는 경로). */
  onAssignMember: (index: number, memberId: string) => void;
  /** F-G: 연락처 없는 인원을 골랐을 때 — 입력 폼을 열고, 저장되면 그 칸에 지정한다 */
  onFixMemberContact: (member: { id: string; name: string }, index?: number) => void;
  /** 이미 있는 칸의 담당자를 작성자 본인으로 지정. */
  onAssignAuthor: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => void;
  onLabelChange: (index: number, value: string) => void;
  onToggleNotify: (index: number, kind: NotifyKind) => void;
  getStepLabel: (index: number) => string;
  getDefaultLabel: (index: number) => string;
  /** 결재 단계 최대 추가 가능 수 (undefined = 무제한). 전체 칸 상한(locked + 추가) 판정용. */
  maxApprovers?: number;
  /**
   * 양식이 정의한 칸 **외에** 더 만들 수 있는 칸 수(admin이 정한 `maxAdditionalLines`).
   * "최대 N명까지 추가 가능합니다" 안내 문구의 N으로 사용한다.
   * maxApprovers(전체 칸 = locked + 양식 단독 비-locked + maxAdditionalLines)와 구분된다.
   */
  maxAdditionalLines?: number;
  /** 첫 단계 고정 라벨. locked=true면 라벨 수정/삭제/이동 불가 */
  requiredFirstStep?: { label: string; locked: boolean };
  /** true이면 미리보기 이름 행에서 1단계(locked) 이름을 표시하지 않음 */
  hideFirstLineName?: boolean;
  /** 저장 실패 시 표시할 에러 메시지 */
  errorMessage?: string | null;
  /**
   * PENDING_APPROVAL **대기 칸 지정 모드**(결정 2026-08-27 park-and-resume C2).
   * true면 대기 칸(`_parkedFill` 행)의 담당자 지정만 열고 나머지는 전부 잠근다 —
   * 단계 추가·삭제·순서·라벨·담당자 해제·통지 채널 변경 불가(서버가 같은 범위로 거부한다:
   * `APPROVAL_LINE_PARKED_ONLY_EDIT`). 지정 select도 member만 내놓는다(서버 fill 축이 member 하나다).
   */
  parkedFillMode?: boolean;
  /**
   * **위임 흐름 안에서 열린 편집인가** — 체인 머리가 `delegatedTo`인 칸의 표시·조작을 가른다
   * (2026-09-09). `undefined`면 위임 흐름이 아니다(보고서 상세 편집 등) → 종전 동작 그대로.
   *
   * - `'pending'`: 위임 담당자를 아직 안 골랐다. 그 칸의 이름 자리에 폴백(작성자)을 사람 이름으로
   *   보여 주면 **거짓**이다 — 저장하면 서버가 위임 담당자로 해소하므로 미리보기만 다른 사람을
   *   가리킨다(2026-09-09 dev: 김대표를 골랐는데 1행은 임경업이었다). 「위임 담당자」로 쓴다.
   * - `'assigned'`: 골랐다. 이름은 그 사람 그대로.
   *
   * 두 값 모두 **그 칸의 담당자 변경 select를 내지 않는다.** 그 자리의 값은 위임 탭이 소유하고,
   * 여기서 다른 사람을 고르면 저장 시 서버 해소가 이겨 「골랐는데 원래대로」가 된다.
   */
  delegationSlot?: 'pending' | 'assigned';
  /** true이면 "이 현장 기본 결재선으로 저장" 체크박스를 표시 */
  showSaveAsSiteDefault?: boolean;
  /** "이 현장 기본 결재선으로 저장" 체크박스 현재 값 */
  saveAsSiteDefault?: boolean;
  /** "이 현장 기본 결재선으로 저장" 체크박스 토글 핸들러 */
  onToggleSaveAsSiteDefault?: (v: boolean) => void;
  /** true이면 "현장 기본으로 되돌리기" 버튼을 표시 (report 모드, 보고서별 결재선이 있을 때) */
  showResetToSiteDefault?: boolean;
  /** "현장 기본으로 되돌리기" 버튼 클릭 핸들러 */
  onResetToSiteDefault?: () => void;
}

/**
 * 대기 칸 지정 모드에서 **이미 요청이 나간(또는 서명된) 칸**의 채널 체크박스에 다는 잠금 사유.
 *
 * ⚠️ "재전송 때 다시 고를 수 있다"고 쓰면 거짓이다 — 결재 알림 재전송
 * (`ReportApprovalService.resendApprovalNotification`)은 채널을 고르는 UI가 없고, 저장된
 * `notifyVia`(content 우선 → 정의 행)를 그대로 읽어 보낸다. 그래서 "처음 고른 채널로 나간다"고
 * 말한다. 잠금 자체의 근거: 서버 좁은 경로가 비대기 칸의 `notifyVia`를 읽지 않는다.
 */
export const SENT_STEP_CHANNEL_LOCKED_TITLE =
  '이미 결재 요청이 나간 단계는 통지 채널을 바꿀 수 없습니다. 재전송도 처음 고른 채널로 나갑니다.';

export function EditModal({
  overlayZClassName,
  overlayBgClassName,
  editingLines,
  availableMembers,
  isSaving,
  isDirty = true,
  draggedIndex,
  dragOverIndex,
  onClose,
  onSave,
  onAddDropdownChange,
  onAddUnassignedSlot,
  onRemoveApprover,
  onUnsetAssignee,
  onAssignMember,
  onFixMemberContact,
  onAssignAuthor,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onLabelChange,
  onToggleNotify,
  getStepLabel,
  getDefaultLabel,
  maxApprovers,
  maxAdditionalLines,
  requiredFirstStep,
  hideFirstLineName = false,
  errorMessage,
  parkedFillMode = false,
  delegationSlot,
  showSaveAsSiteDefault = false,
  saveAsSiteDefault = false,
  onToggleSaveAsSiteDefault,
  showResetToSiteDefault = false,
  onResetToSiteDefault,
}: EditModalProps) {
  // 한도 미상(maxApprovers === undefined)이면 비활성화하지 않는다 — 기존 동작 유지.
  const isAtMaxApprovers = maxApprovers !== undefined && editingLines.length >= maxApprovers;
  /** 한도 도달 안내 문구 — 목록 상태/빈 상태 양쪽에서 동일하게 사용. */
  const maxReachedMessage =
    maxAdditionalLines !== undefined
      ? `최대 ${maxAdditionalLines}명까지 추가 가능합니다.`
      : '더 이상 추가할 수 없습니다.';

  /**
   * 라인 표시용 이름: `member.name`(신원 없는 표시용 member 포함) → 단계 라벨 → 서버 플레이스홀더.
   * 결재란 미리보기 칸·삭제 확인 문구도 같이 쓰므로 여기에는 안내를 덧붙이지 않는다
   * (미리보기는 실제 결재란 모습이어야 하고, 좁은 칸이라 잘린다). 신원 유실 안내는
   * 편집 목록 행에서만 IDENTITY_LOST_NAME_SUFFIX로 덧붙인다.
   */
  /**
   * 이 칸의 담당자를 **위임 탭이 소유하는가** (2026-09-09).
   *
   * 두 재료의 AND다 — 칸의 성질(`체인 머리가 delegatedTo`)과 화면의 맥락(`위임 흐름인가`).
   * 칸 성질만으로 가르면 **위임하지 않은 보고서**의 같은 칸까지 잡는다. 거기선 폴백(작성자)이
   * 최종 답이라 「위임 담당자」로 쓰면 그쪽이 거짓이 된다.
   */
  const isDelegationOwnedSlot = (line: ApprovalLineItem) =>
    delegationSlot !== undefined && isDelegationOwnedStep(line);

  /**
   * 위임 담당자를 **아직 안 고른** 칸 — 서버가 폴백(작성자)으로 해소해 보내지만, 저장하면
   * 위임 담당자가 그 자리를 가져간다. 즉 이 행의 **모든 사람 정보가 임시값**이다.
   *
   * 이름·연락처·채널이 이 술어를 함께 참조한다 — 하나만 가르면 「이름은 미정인데 연락처는
   * 임경업」이라는 반쪽 진실이 된다(2026-09-09 사용자 적발).
   */
  const isPendingDelegationSlot = (line: ApprovalLineItem) =>
    isDelegationOwnedSlot(line) && delegationSlot === 'pending';

  /**
   * 위임 담당자를 아직 안 고른 칸의 이름 — **사람 이름이 아니라 자리 이름**이다.
   *
   * 폴백(작성자)을 그대로 보여 주면 저장 결과와 다른 사람을 가리킨다(2026-09-09 dev: 김대표를
   * 골랐는데 1행은 임경업이었다). 그 자리가 아직 미정임을 말하는 편이 정직하다.
   */
  const getLineName = (line: ApprovalLineItem) =>
    isPendingDelegationSlot(line)
      ? '위임 담당자'
      : (line.member?.name ?? line.label ?? PARKED_SLOT_SIGNER_NAME);

  /**
   * 라인 표시용 연락처: `member` 축 하나(구 프로필 폴백은 2026-09-16 E4에서 삭제).
   *
   * **위임이 소유한 칸은 비운다 — 담당자를 고른 뒤에도 마찬가지다.**
   * 이 칸의 사람 정보는 위임 탭이 소유하므로 결재란이 그것을 다시 그리지 않는다.
   * 상태마다 이유는 다르지만 처방은 같다:
   *  - **미정**: 폴백(작성자)의 전화·이메일이라 **남의 연락처**다. 이름만 「위임 담당자」로
   *    갈라 놓으면 반쪽 진실이 된다(2026-09-09 사용자 적발).
   *  - **지정됨**: 연락처 자체는 맞지만 **바로 위 「담당자에게 위임」 섹션이 이미 보여 준다**
   *    — 그것도 `SMS (010-…)`·`이메일 (…)` 체크박스로 **고른 채널만 켜진 채로**. 여기서 다시
   *    늘어놓으면 중복인 데다 선택 상태를 반영하지 않아 **안 고른 채널까지 나열**된다
   *    (2026-09-10 사용자 적발). 그 칸엔 결재 요청도 안 나가므로(getLineKindOptions 주석)
   *    「이 채널로 나갑니다」로 읽히는 줄 자체가 거짓 신호다.
   *
   * 비우면 표시 게이트(`contact.phone || displayEmail`)가 연락처 줄과 채널 선택을 통째로
   * 접는다 — 신원 확인은 이름이 하고, 연락처·채널은 위임 탭 한 곳에서만 말한다.
   */
  const getLineContact = (line: ApprovalLineItem) =>
    isDelegationOwnedSlot(line)
      ? { phone: null, email: null, accountEmail: null }
      : {
          phone: line.member?.phone ?? null,
          email: line.member?.email ?? null,
          accountEmail: line.member?.accountEmail ?? null,
        };

  /**
   * 이 칸이 고를 수 있는 채널 **종류**(출처 축 제거, 2026-08-23). 기본 선택 = 가용 전부라
   * `defaultNotifyVia`가 곧 가용 종류다. 계정 로그인 이메일(accountEmail)도 이메일의 근거다 —
   * 서버 발송 사다리의 마지막 칸이라 종류만으로도 실제 도달한다.
   *
   * ⚠️ **위임이 소유한 칸엔 고를 채널이 없다 — 담당자를 이미 골랐어도 마찬가지다.**
   * 이 체크박스가 정하는 것은 그 칸의 **결재 요청** 채널(`notifyVia`)인데, 위임 칸은 제출
   * 시점에 작성 서명으로 preset 승인된다(`isSlotFilledByAuthoringSignature`: author 칸과
   * `sourceType==='delegatedTo'`를 둘 다 true로 본다 + 제출은 `authorSignedAt`을 무조건
   * 요구한다). 그래서 그 칸은 **첫 PENDING이 될 수 없고**, 결재 요청이 영영 나가지 않는다 —
   * `notifyVia`의 소비처가 0이다. 그 사람에게 실제로 나가는 것은 **위임 링크**이고, 그 채널은
   * 위임 탭의 `notifyChannels`가 정본이다(다른 축이라 이름도 다르다).
   * 고를 수는 있는데 아무 일도 하지 않는 컨트롤이라 감춘다 — 대신 안내가 어디서 정하는지 말한다.
   */
  const getLineKindOptions = (line: ApprovalLineItem): NotifyKind[] =>
    isDelegationOwnedSlot(line) ? [] : (defaultNotifyVia(getLineContact(line)) ?? []);

  /**
   * 그 종류가 켜져 있는가 — 정본 축(`notifyVia`, 2026-08-23 단일화)을 `selectedNotifyKinds`로
   * 읽고, 미지정(제한 없음)이면 가용 채널 전부를 켜진 것으로 본다(서버 폴백 판정과 동형).
   */
  const isKindOn = (line: ApprovalLineItem, kind: NotifyKind): boolean =>
    (selectedNotifyKinds(line) ?? defaultNotifyVia(getLineContact(line)) ?? []).includes(kind);

  /** 이 칸에 담당자가 지정되지 않았는가(= 오프라인 수기 결재 칸). */
  const isUnassigned = (line: ApprovalLineItem) => line._assigneeUnset === true;

  /**
   * 이 행이 대기 칸 지정 모드에서 **지정을 받는 그 칸**인가(`ApprovalLineSectionImpl`이
   * 대기 서명행 level에 stamp). 지정 후에도 이 표식으로 select를 유지해 해제 없이
   * 다른 사람으로 바꿀 수 있다.
   */
  const isParkedFillLine = (line: ApprovalLineItem) =>
    (line as ApprovalLineItem & { _parkedFill?: boolean })._parkedFill === true;

  /**
   * 담당자 지정은 **앞에서부터 연속(prefix)** 이어야 한다(결정 2026-08-13 §2).
   * 첫 미지정 칸의 index — 그 뒤 칸은 담당자를 지정할 수 없다(지정해도 서버가 400으로 거부).
   * 미지정 칸이 없으면 -1.
   *
   * 순차 결재에서 미지정(오프라인) 칸의 완료 신호는 시스템 밖에서 발생하므로, 중간에 끼면
   * 다음 단계를 깨울 트리거가 사라진다 — 그래서 지정 구간은 항상 앞쪽 연속이어야 한다.
   */
  const firstUnassignedIndex = editingLines.findIndex(isUnassigned);
  /** 미지정 칸이 하나라도 있는가 — 있으면 "칸 추가"(항상 지정된 칸을 만든다)를 막는다. */
  const hasUnassignedSlot = firstUnassignedIndex >= 0;

  /**
   * 이 칸에 해당 소스로 담당자를 지정할 수 있는가. whitelist 미지정 = 제한 없음.
   * 해제(빈 체인)는 whitelist와 무관하게 항상 허용이므로 여기서 묻지 않는다.
   *
   * 서버 `enforceLockedFromSystem`이 위반 값을 거부하되 **조용히 admin 값으로 되돌리므로**,
   * 화면이 이걸 모르면 사용자는 "바꿨는데 안 바뀐다"를 겪는다. 선택지 자체를 내놓지 않는다.
   *
   * ⚠️ 제한은 **행에 새겨져 있다**(`_assigneeSourceWhitelist`, `initEdit`에서 stamp).
   * 위치(index)로 preview baseRows를 되찾으면 앞 칸을 지우거나 순서를 바꾼 뒤 뒤 칸들이
   * 남의 제한을 쓰게 된다 — 그 칸에서만 선택지가 사라지거나, 서버가 거부할 값을 내놓는다.
   *
   * ⚠️ 제한은 하나 더 있다 — **자리**(S77, 결정
   * `2026-08-27-author-delegated-sources-first-slot-only.md`). 첫 칸이 아니면 `작성자`를 세울 수
   * 없다: 뒤 칸의 작성자 칸은 작성자가 서명하는 순간 자동으로 채워져 **제3자 결재를 한 번도
   * 거치지 않고** 서명 이미지만 한 장 더 찍는다. 판정은 서버와 같은 공유 술어
   * (`isAssignmentSourceAllowedAtSlot`)에 맡긴다 — 두 벌이면 화면만 막고 서버는 통과한다.
   *
   * ⚠️ 뒤 칸의 `{role}`이 작성자 본인으로 해소되는 것은 **막지 않는다**(S12·S20-b·S20-c).
   * 이 select는 소스 종류를 고르는 자리이지 해소 결과를 정하는 자리가 아니다.
   */
  const allowsAssigneeSource = (
    line: ApprovalLineItem,
    source: 'author' | 'member',
    index: number,
  ): boolean => {
    // 대기 칸 지정 모드의 지정 축은 **member 하나**다 — 서버 fill 경로가 memberId만 받고
    // whitelist를 보지 않으므로(진행 중 문서의 멈춘 칸을 사람으로 채우는 좁은 동작),
    // 화면도 같은 범위만 내놓는다. author를 내놓으면 payload에 memberId가 없어
    // "지정 안 함"으로 떨어진다(조용한 무시 — 선택지 자체를 없애는 쪽이 정직하다).
    if (parkedFillMode) return source === 'member';
    const whitelist = (line as ApprovalLineItemWithWhitelist)._assigneeSourceWhitelist;
    return (
      (!whitelist || whitelist.includes(source)) &&
      isAssignmentSourceAllowedAtSlot(source, index)
    );
  };

  /** whitelist·자리 때문에 이 칸에 지정할 수 있는 소스가 하나도 없으면 select 자체가 무의미하다. */
  const hasAnyAssignableSource = (line: ApprovalLineItem, index: number): boolean =>
    allowsAssigneeSource(line, 'author', index) ||
    allowsAssigneeSource(line, 'member', index);

  /**
   * 그 자리에서 담당자를 지정하는 select — **미지정 칸과 미해소 칸이 공유**한다.
   *
   * 왜 공유인가(S66, 2026-08-27): 이 모달은 `lineUnassigned ? (…select…) : (…이름…)` 두
   * 갈래인데 **미해소 칸은 "지정됨" 쪽으로 떨어져** 지정 수단이 아예 없었다. 쓸 수 있는
   * 액션이 `담당자 해제`(= 역할 선언을 버리고 종이 결재로 강등) 하나뿐이라, "막을 거면
   * 그 자리에서 고칠 길을 준다"는 S66 계약을 위반하고 있었다.
   *
   * ⚠️ 선택지는 **양식이 정한 whitelist**(`_assigneeSourceWhitelist`)를 그대로 따른다.
   * 넓히면 서버 `enforceLockedFromSystem`이 조용히 되돌려 "저장했는데 원래대로"가 된다.
   *
   * `variant`는 **문구만** 가른다(2026-09-09) — 빈 칸을 채우는 것과 이미 있는 사람을 바꾸는
   * 것은 사용자가 하려는 일이 다르므로 그렇게 읽혀야 한다. 판정·선택지·연락처 없는 인원
   * 처리는 한 벌 그대로다.
   */
  const renderAssigneeSelect = (
    line: ApprovalLineItem,
    index: number,
    disabled: boolean,
    variant: 'assign' | 'replace' = 'assign',
  ) => {
    if (!hasAnyAssignableSource(line, index)) return null;
    const actionLabel = variant === 'replace' ? '담당자 변경' : '담당자 지정';
    /**
     * 교체 목록에서는 **지금 그 칸에 있는 사람을 뺀다** — 고를 수는 있는데 아무 일도 일어나지
     * 않는 항목이기 때문이다.
     *
     * 빼는 것 자체보다 **빼고 나면 남는 게 없는 칸**이 있다는 게 요점이다(2026-09-09 dev 실측):
     * 위임 모달 1행은 체인이 `위임 담당자|작성자`라 whitelist가 내주는 소스가 author뿐인데
     * 그 자리에 이미 작성자가 들어가 있어, 교체 select에 **무효 항목 하나만** 떴다. 바꿀 수
     * 있는 게 없는 칸에 바꾸라는 컨트롤을 두면 사용자는 왜 안 바뀌는지를 찾게 된다.
     *
     * 지정(`assign`) 갈래는 손대지 않는다 — 빈 칸에는 뺄 「현재」가 없다.
     */
    const replacing = variant === 'replace';
    const memberChoices = replacing
      ? availableMembers.filter((m) => getMemberId(m) !== line.memberId)
      : availableMembers;
    const showAuthor =
      allowsAssigneeSource(line, 'author', index) && !(replacing && line.sourceType === 'author');
    /**
     * ⚠️ 「목록이 비면 안 낸다」는 **교체 갈래에만** 건다. 지정 갈래에서 같은 규칙을 걸었더니
     * 조직 멤버가 없거나 아직 안 실린 상태에서 **지정 수단이 통째로 사라졌다**(S77 대조군
     * spec이 잡았다) — 빈 칸에 지정 select가 없으면 그게 막다른 길이다.
     * 교체는 반대다: 현재 담당자를 뺀 뒤 남는 게 없으면 바꿀 대상이 없다는 뜻이라 안 내는 게 맞다.
     */
    const showMembers =
      allowsAssigneeSource(line, 'member', index) && (!replacing || memberChoices.length > 0);
    if (replacing && !showAuthor && !showMembers) return null;
    return (
      <select
        value=""
        disabled={disabled}
        onChange={(e) => {
          const value = e.target.value;
          if (!value) return;
          if (value === '__author__') { onAssignAuthor(index); return; }
          // F-G: 연락처가 없는 인원이면 지정 대신 **입력 폼**을 연다.
          // 저장되면 훅이 이 칸(index)에 그대로 지정한다.
          const picked = availableMembers.find((m) => getMemberId(m) === value);
          if (picked && isUnreachableMember(picked)) {
            onFixMemberContact(
              { id: getMemberId(picked), name: picked.member?.name ?? picked.user?.name ?? '-' },
              index,
            );
            return;
          }
          onAssignMember(index, value);
        }}
        className="mt-1.5 w-full py-1.5 px-2 text-sm border border-dashed border-ink-300 rounded-control focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-ink-500 bg-white hover:border-ink-400 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface-soft"
        aria-label={`${getStepLabel(index)} ${actionLabel}`}
      >
        <option value="" disabled>{actionLabel}...</option>
        {/* 허용된 소스만 내놓는다 — 서버가 거부하는 값을 고를 수 있게
            두면 "저장했는데 원래대로 돌아온다"가 된다. */}
        {showAuthor && <option value="__author__">작성자 본인</option>}
        {showMembers && <MemberOptions availableMembers={memberChoices} />}
      </select>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="결재라인 편집"
      size="xl"
      // 오버레이·Esc·✕·포커스 트랩·aria·스크롤 잠금은 base Modal이 소유한다.
      // isDirty면 모든 닫기 요청(Esc·오버레이·✕·footer 취소)이 확인 모달을 거친다
      // (수정 모달 dirty 표준 — 결정 2026-08-16 ②). 저장 중에는 닫기 수단을 전부 잠근다.
      // 층은 여는 쪽이 주입한다(자체 계산 없음 — props doc 참조).
      overlayZClassName={overlayZClassName}
      overlayBgClassName={overlayBgClassName}
      isDirty={isDirty}
      closeLocked={isSaving}
      // 긴 결재라인 목록이 뷰포트를 넘칠 때 **본문만** 스크롤한다 — 헤더(✕)와 푸터
      // (초기화·취소·저장)는 고정이다. 종전 값은 `panelClassName="max-h-[90vh] overflow-y-auto"`로
      // **패널**을 스크롤시켜 단계가 많은 결재라인에서 그 버튼들이 함께 밀렸다(web 어댑터가
      // 아직 그 값을 들고 있는 것은 소비처가 푸터를 children에 넣는 API라 선행 전환이 필요해서다 —
      // 그 자리를 근거로 이 값을 되돌리지 말 것).
      //
      // ⚠️ **클리핑 컨텍스트가 패널 → 본문으로 한 겹 안쪽으로 옮겨진다**(`overflow-y-auto`가
      // 새 클리핑 컨텍스트를 만든다) — 그래서 치환 전에 「본문 안 절대배치 자식이 본문 경계를
      // 넘을 수 있나」를 셌다: **0개다.** 이 파일 코드에 `absolute`·`fixed`·`createPortal`·
      // `transform`이 0건이고, 본문이 쓰는 자식은 `Button`·`Badge`·인라인 SVG(전부 위치지정 0)와
      // **네이티브 `<select>`**(`MemberOptions`)뿐이다 — 네이티브 select 팝업은 브라우저 크롬이
      // 그리므로 CSS overflow에 **원리적으로** 안 잘린다. 단계 재배열도 **HTML5 네이티브 DnD**라
      // (`handleDragStart`가 `effectAllowed`와 원본 `opacity`만 만지고 `setDragImage`·
      // `document.createElement`는 0건) 드래그 이미지가 DOM 요소가 아니라 브라우저 스냅샷이다.
      // ⚠️ 이 축은 **tsc·spec·프로브 어느 것도 못 본다**(jsdom에 레이아웃이 없다) — 판정 근거는
      // 코드 독해뿐이니, 커스텀 드롭다운·툴팁·달력을 본문에 넣게 되면 **그때 이 판정을 다시 하라**.
      bodyScroll
      footer={
        <ApprovalEditModalFooter
          showReset={showResetToSiteDefault}
          onReset={onResetToSiteDefault}
          isSaving={isSaving}
          isDirty={isDirty}
          onSave={onSave}
          onFallbackClose={onClose}
        />
      }
    >

          {/* 대기 칸 지정 모드 안내 — 왜 대부분이 잠겨 있는지 입구에서 말한다. */}
          {parkedFillMode && (
            <div className="mb-4 rounded-card border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              결재가 진행 중인 문서입니다. 담당자가 비어 대기 중인 결재란의 <strong>담당자 지정</strong>만
              할 수 있고, 나머지 결재란과 단계 구성은 잠겨 있습니다.
            </div>
          )}

          {/* 결재란 미리보기 — author 일원화(2026-06-10): editingLines가 정의의 author 단계(담당자)를
              일반 칸으로 포함하므로 별도 "담당" 칸을 두지 않고 editingLines만 렌더한다.
              상세(ApprovalDisplayTable)와 동일하게 정의 칸 = 결재 칸. */}
          <div className="mb-5">
            <p className="text-xs text-ink-400 mb-2">미리보기</p>
            <div className="flex flex-col items-center">
              <div className="inline-flex border border-ink-800">
                <div className="border-r border-ink-800 px-1.5 flex items-center justify-center text-[10px] font-semibold text-ink-700 leading-tight text-center">
                  결<br />재
                </div>
                {editingLines.map((line, index) => (
                  <div key={`preview-${index}`} className={`w-16 text-center ${index < editingLines.length - 1 ? 'border-r border-ink-800' : ''}`}>
                    <div className="bg-surface-soft border-b border-ink-400 py-0.5 text-[10px] font-medium text-ink-600">
                      {getStepLabel(index)}
                    </div>
                    <div className="h-9 flex items-center justify-center">
                      {/* 미지정 칸은 종이로 받는 칸 — 전자 서명이 들어올 자리가 아니라는 것이
                          미리보기에서도 드러나야 한다("수기"). 실제 출력물에서는 이 칸이
                          완전한 빈 서명란으로 나간다. */}
                      <span className={`text-[9px] ${isUnassigned(line) ? 'text-amber-500' : 'text-ink-300'}`}>
                        {isUnassigned(line) ? '수기' : '서명'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {editingLines.length > 0 && (
                <div className="inline-flex mt-0.5">
                  <div className="px-1.5 invisible text-[10px]">결</div>
                  {editingLines.map((line, index) => (
                    <div key={`name-${index}`} className="w-16 flex items-center justify-center">
                      {/* 이름이 메인(font-medium)으로 강조.
                          미지정 칸은 이름 자리도 비워 둔다 — getLineName은 label로 fallback해서
                          "검토" 같은 단계명이 사람 이름처럼 보인다(렌더 SSOT buildMergedItems와 동형). */}
                      <span className="text-[9px] font-medium text-ink-700 truncate px-0.5">
                        {(hideFirstLineName && index === 0) || isUnassigned(line) ? '' : getLineName(line)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {editingLines.length === 0 ? (
            <div className="border-2 border-dashed border-ink-300 rounded-card py-10 text-center">
              <div className="text-ink-400 mb-3">
                <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-sm text-ink-500 mb-3">등록된 결재란이 없습니다.</p>
              {/* 한도가 0인 정책(추가 불가)에서도 컨트롤이 활성으로 보이지 않도록 비활성 + 사유 안내 */}
              {isAtMaxApprovers && (
                <p className="text-sm text-ink-400 mb-3">{maxReachedMessage}</p>
              )}
              {/* 칸이 0개인 보고서에서도 **칸 추가**가 주 액션이어야 한다 — 종전에는 이 자리에
                  '결재자 추가' select뿐이라, 담당자를 지정해야만 칸이 생겼다. 즉 미지정(종이)
                  칸으로 시작하는 결재란을 아예 만들 수 없었다. 칸 추가와 담당자 지정은 별개
                  축이므로(결정 2026-08-13) 목록 상태와 같은 두 경로를 여기에도 둔다.
                  목록이 비어 있어 prefix(담당자는 앞에서부터 연속)는 어느 쪽도 어기지 않는다. */}
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  disabled={isAtMaxApprovers}
                  onClick={onAddUnassignedSlot}
                  className="min-w-[200px] py-2.5 px-3 text-sm border border-dashed border-ink-300 rounded-control text-ink-500 bg-white hover:border-ink-400 hover:text-ink-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface-soft disabled:hover:border-ink-300 disabled:hover:text-ink-500"
                >
                  + 결재란 추가
                </button>
                <p className="text-xs text-ink-400">
                  빈 칸으로 추가됩니다. 담당자는 각 칸의 <strong>담당자 지정</strong>에서 정하세요
                  (비워 두면 종이로 결재).
                </p>
                <select
                  value=""
                  disabled={isAtMaxApprovers}
                  onChange={(e) => {
                    const value = e.target.value;
                    // F-G: 칸 추가 경로도 같은 규칙 — 연락처 없는 인원을 고르면 입력 폼을 연다.
                    // (칸 index를 주지 않으면 저장 후 목록 끝에 새 칸으로 추가된다.)
                    const picked = availableMembers.find((m) => getMemberId(m) === value);
                    if (picked && isUnreachableMember(picked)) {
                      onFixMemberContact({
                        id: getMemberId(picked),
                        name: picked.member?.name ?? picked.user?.name ?? '-',
                      });
                      return;
                    }
                    onAddDropdownChange(value);
                  }}
                  className="px-4 py-2 text-sm border border-line rounded-control focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-ink-700 bg-white min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface-soft"
                >
                  <option value="" disabled>담당자를 지정해 추가...</option>
                  <MemberOptions availableMembers={availableMembers} />
                  <option value="__new__">+ 현장·업체 담당자 추가</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {editingLines.map((line, index) => {
                // _locked===true인 행은 admin 고정 단계 — 라벨/삭제/이동/드래그 불가.
                // requiredFirstStep.locked는 하위호환 (첫 단계 고정 전용 prop).
                // 대기 칸 지정 모드에서는 **전 행**이 구조 잠금이다 — 열리는 것은 대기 칸의
                // 담당자 지정 select뿐(서버가 그 외 접촉을 거부하므로 화면도 같은 범위).
                const isLineLocked =
                  parkedFillMode ||
                  (line as ApprovalLineItem & { _locked?: boolean })._locked === true ||
                  (index === 0 && requiredFirstStep?.locked === true);
                // 바로 위 단계가 locked이면 해당 행의 "위로" 이동도 차단 (locked 단계와 swap 방지)
                const prevLineLocked =
                  index > 0 &&
                  ((editingLines[index - 1] as ApprovalLineItem & { _locked?: boolean })._locked === true ||
                    (index - 1 === 0 && requiredFirstStep?.locked === true));
                const contact = getLineContact(line);
                const kindOptions = getLineKindOptions(line);
                // 대기 칸 지정 모드에서 채널을 잠그는 것은 **비대기 칸**뿐 — 지정을 받는 대기 칸
                // 자기 행(_parkedFill)은 서버가 지정과 함께 채널을 받는다(2026-08-31 개정).
                const channelLocked = parkedFillMode && !isParkedFillLine(line);
                // 연락 이메일이 없어도 계정 로그인 이메일이 있으면 그 주소로 통지가 간다 —
                // 표시 줄도 그 폴백을 따라야 "계정 이메일만 있는 결재자" 행이 비지 않는다.
                const displayEmail = contact.email ?? contact.accountEmail;
                const lineUnassigned = isUnassigned(line);
                // 앞 칸이 비어 있으면 이 칸은 담당자를 지정할 수 없다(prefix 규칙).
                const prefixBlocked =
                  firstUnassignedIndex >= 0 && index > firstUnassignedIndex;
                // 앞이 비었는데 이 칸엔 담당자가 있다 = 저장 불가 상태. 자동으로 지우지 않고
                // (남의 입력을 말없이 없애지 않는다) 표시로 알리고 저장에서 막는다.
                const prefixViolating = prefixBlocked && !lineUnassigned;
                return (
                  <div
                    key={`${line.memberId ?? ''}-${index}`}
                    onDragOver={isLineLocked ? undefined : (e) => onDragOver(e, index)}
                    onDragLeave={isLineLocked ? undefined : onDragLeave}
                    onDrop={isLineLocked ? undefined : (e) => onDrop(e, index)}
                    className={`border rounded-card overflow-hidden transition-all ${
                      dragOverIndex === index && !isLineLocked
                        ? 'ring-2 ring-brand-500 bg-brand-50'
                        : lineUnassigned
                          // 미지정 칸: 점선 + amber로 "채워지지 않은 칸"임을 목록에서도 구분.
                          // locked 색(brand)보다 우선한다 — locked 여부보다 담당자 유무가
                          // 지금 사용자가 판단해야 할 것이다.
                          ? 'border-dashed border-amber-300 bg-amber-50'
                          : isLineLocked
                            ? 'border-brand-200 bg-brand-50'
                            : 'border-line'
                    } ${draggedIndex === index ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center">
                      <div
                        draggable={!isLineLocked}
                        onDragStart={isLineLocked ? undefined : (e) => onDragStart(e, index)}
                        onDragEnd={isLineLocked ? undefined : onDragEnd}
                        className={`flex-shrink-0 w-24 border-r border-line px-1 py-2 self-stretch flex flex-col items-center justify-center gap-1 ${
                          isLineLocked
                            ? 'bg-brand-50 cursor-not-allowed'
                            : 'bg-surface-soft cursor-grab active:cursor-grabbing'
                        }`}
                      >
                        {isLineLocked ? (
                          <LockIcon className="w-4 h-4 text-brand-400" />
                        ) : (
                          <DragHandleIcon className="w-4 h-4 text-ink-400" />
                        )}
                        {isLineLocked ? (
                          <span className="text-center text-xs font-semibold text-brand-700 px-1">
                            {/* locked 단계: label을 그대로 표시.
                                ⚠️ `requiredFirstStep.label`은 **폴백이지 정본이 아니다.** 종전에는
                                `index === 0`이면 무조건 이겨서, 메타가 준 라벨을 덮었다 — 위임
                                모달이 `'안전관리자'`를 하드코딩해 넘기므로(2026-05-22 도입, 그때는
                                COMPOSED 템플릿이 자기 결재라인 메타를 갖기 전이다) `tbm-meeting-log`의
                                1단계가 화면에서 「담당」이 아니라 「안전관리자」로 나왔다. 그 이름은
                                두 번 틀렸다: 그 칸은 **위임 담당자 칸**이고, 안전관리자는 **2단계**다.
                                라벨 정본은 언제나 메타(`line.label`)이고, 이 prop은 메타에 라벨이
                                없는 구 경로를 위한 하위호환 backfill이다. 바로 아래 편집 가능한
                                입력이 이미 `line.label` 우선이라 두 갈래가 갈라져 있기도 했다. */}
                            {line.label ??
                              (index === 0 ? requiredFirstStep?.label : undefined) ??
                              getDefaultLabel(index)}
                          </span>
                        ) : (
                          <input
                            type="text"
                            value={line.label ?? getDefaultLabel(index)}
                            onChange={(e) => onLabelChange(index, e.target.value)}
                            placeholder={getDefaultLabel(index)}
                            onFocus={(e) => e.target.select()}
                            className="w-full text-center text-xs font-semibold text-ink-700 bg-transparent border-b border-dashed border-ink-300 focus:border-brand-500 focus:outline-none px-1 py-0.5"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                          />
                        )}
                      </div>

                      <div className="flex-1 px-3 py-2.5">
                        {lineUnassigned ? (
                          /* 담당자 미지정 칸 — 칸은 있으나 담당자가 없다(= 종이 결재).
                             "실수로 빈 것"과 구별되도록 배지 + 사유를 함께 보여주고,
                             그 자리에서 담당자를 지정할 수 있게 select를 둔다(칸 추가 select는
                             목록 끝에 새 칸을 만들 뿐이라 중간 칸을 채울 수 없다). */
                          <div className="px-3">
                            <div className="flex items-center gap-2">
                              <Badge tone="amber" shape="pill" size="sm">
                                <span className="inline-flex items-center gap-1">
                                  <UserMinusIcon className="w-3.5 h-3.5" />
                                  {UNASSIGNED_BADGE_LABEL}
                                </span>
                              </Badge>
                              <span className="text-xs text-ink-400">{UNASSIGNED_HINT}</span>
                            </div>
                            {renderAssigneeSelect(line, index, prefixBlocked)}
                            {prefixBlocked && (
                              <p className="mt-1 text-xs text-amber-700">
                                앞 칸이 미지정이라 이 칸에는 담당자를 지정할 수 없습니다. 앞 칸부터 채워 주세요.
                              </p>
                            )}
                            {!hasAnyAssignableSource(line, index) && (
                              <p className="mt-1 text-xs text-ink-400">
                                이 칸은 양식에서 담당자를 지정할 수 없도록 정해져 있습니다. 종이(수기)로만 결재합니다.
                              </p>
                            )}
                          </div>
                        ) : (
                        <>
                        <div className="flex items-center">
                          <span className="flex-1 px-3 py-0.5 text-sm text-ink-700">
                            {getLineName(line)}
                            {/* 직함도 이름과 같은 술어로 갈린다 — 「위임 담당자 (안전관리자)」는
                                자리 이름에 남의 직함이 붙은 것이다(연락처와 같은 계열). */}
                            {!isPendingDelegationSlot(line) && line.member?.title
                              ? ` (${line.member.title})`
                              : ''}
                            {/* 신원 유실 단계(2026-07-24 사고 백필분): 이름이 서명행에서 복원돼
                                정상으로 보이지만 저장 시 결재자 단계가 유지되지 않는다. 사용자가
                                무엇을 해야 하는지 알 수 있게 표시한다(저장하면 상단 안내로도 막힌다). */}
                            {isIdentityLostApprovalStep(line) ? IDENTITY_LOST_NAME_SUFFIX : ''}
                            {/* I-1: 미해소 칸은 이름 자리에 서버 폴백(PARKED_SLOT_SIGNER_NAME)이 떠 사람
                                이름처럼 보인다. 신원 유실과 같은 방식으로 이름 옆에만 알린다
                                (미리보기 칸은 실제 결재란 모습이어야 하므로 건드리지 않는다). */}
                            {isUnresolvedApprovalStep(line) ? UNRESOLVED_NAME_SUFFIX : ''}
                          </span>
                        </div>
                        {prefixViolating && (
                          <p className="px-3 mt-0.5 text-xs text-amber-700">
                            앞 칸이 미지정이라 이대로 저장할 수 없습니다. 이 칸의 담당자도 해제하거나 앞 칸에 담당자를 지정해 주세요.
                          </p>
                        )}
                        {(contact.phone || displayEmail) && (
                          <div className="px-3 mt-0.5">
                            <div className="text-xs text-ink-400">
                              {contact.phone && (
                                <span>{formatPhone(contact.phone)}</span>
                              )}
                              {contact.phone && displayEmail && (
                                <span className="mx-1">&middot;</span>
                              )}
                              {displayEmail && (
                                <span>{displayEmail}</span>
                              )}
                            </div>
                            {/*
                              출처 축 제거(2026-08-23): 채널은 **종류 단위**(SMS/이메일)로
                              렌더한다. 종류가 1개뿐이면 고를 게 없으므로 감춘다(끄는 것은
                              F-F에서 경고와 함께 다룬다).
                            */}
                            {kindOptions.length > 1 && (
                              <div className="flex flex-wrap items-center gap-3 mt-1">
                                {kindOptions.map((kind) => (
                                  <label
                                    key={kind}
                                    className={`flex items-center gap-1 text-xs text-ink-500 ${
                                      channelLocked ? 'cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                    // 잠근 이유를 그 자리에서 말한다 — prod 과천 일지(2026-08-31)에서
                                    // 사용자가 비활성만 보고 "왜"를 알 수 없었다.
                                    title={channelLocked ? SENT_STEP_CHANNEL_LOCKED_TITLE : undefined}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isKindOn(line, kind)}
                                      // 대기 칸 지정 모드에서 **비대기 칸**(이미 요청이 나갔거나 서명된
                                      // 칸)의 채널 변경은 서버가 반영하지 않는다 — 바꿔지는 것처럼
                                      // 보이면 안 되므로 잠근다. 지정을 받는 대기 칸 자기 행은
                                      // 서버가 지정과 함께 채널을 받으므로(2026-08-31 개정) 연다.
                                      disabled={channelLocked}
                                      onChange={() => onToggleNotify(index, kind)}
                                      className="w-3.5 h-3.5 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                                    />
                                    {kind === 'sms' ? 'SMS' : '이메일'}
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {/*
                          F-F: 연락처가 하나도 없는 담당자 — 계정이 있으면 인앱으로는
                          도달하므로 막지 않고 **경고만** 한다(prod 김안전이 그 상태로
                          결재 7건을 실제 처리했다). "로그인한 적 없음"은 근거 데이터가
                          없어(적재가 2026-08부터) 말하지 않고, 로그인해야 본다는 사실만
                          말한다 — 데이터 없이도 참이고 조치를 유도한다.

                          ⚠️ 이 블록은 연락처 표시 게이트(위 contact.phone || displayEmail)
                          **밖**에 있어야 한다 — 연락처가 있으면 종류가 반드시 ≥1이라, 안에
                          두면 절대 렌더되지 않는다(B2 결함: F-F 경고 JSX 도달 불가).
                        */}
                        {/*
                          I-1: **미해소 칸은 이 문구를 쓰면 거짓말이 된다.** 아래 F-F 문구는
                          "인앱으로는 도달한다"를 전제로 조치를 유도하는데, 미해소 칸은 받을
                          사람 자체가 없어 인앱도 가지 않는다. 증상(연락처 종류 0)이 같아 한
                          문구로 묶이기 쉽지만 원인과 조치가 다르다 — 그래서 축을 갈라 각자의
                          문구를 낸다. 2026-08-26 실측 캡처에서 미해소 칸이 F-F 문구를 달고
                          정상처럼 보였다.
                        */}
                        {isUnresolvedApprovalStep(line) || isParkedFillLine(line) ? (
                          <div className="px-3 mt-1 text-xs text-amber-700">
                            {/* 대기 칸 지정 모드는 문구가 다르다 — 문서가 이미 제출돼 이 칸에서
                                멈춰 있으므로 "이대로 제출하면"(제출 전 예고)을 쓰면 거짓이 된다.
                                지정을 마친 칸(_parkedFill + 신원 있음)에는 재개 예고를 준다. */}
                            {isUnresolvedApprovalStep(line)
                              ? parkedFillMode
                                ? PARKED_FILL_ROW_WARNING
                                : UNRESOLVED_ROW_WARNING
                              : '저장하면 이 담당자로 결재가 재개됩니다. 바꾸려면 아래에서 다시 지정하세요.'}
                            {/* S66: **그 자리에서 고칠 길.** 미해소 칸은 이 갈래(지정됨)로
                                떨어져 지금까지 지정 수단이 `담당자 해제`(역할 선언을 버리고
                                종이 결재로 강등)뿐이었다 — 유일한 탈출구가 강등인 것이
                                2026-08-27 결정이 "막다른 길"이라 부른 상태다. 미지정 칸과
                                **같은 select**를 쓴다(선택지는 양식 whitelist가 정한다).
                                대기 칸 지정 모드의 _parkedFill 행은 지정 후에도 select를
                                유지한다 — 해제가 잠겨 있어 이게 유일한 교체 수단이다. */}
                            {renderAssigneeSelect(line, index, prefixBlocked)}
                          </div>
                        ) : (
                          kindOptions.length === 0 &&
                          !isUnassigned(line) &&
                          /* 위임 칸은 이 문구를 쓰면 또 거짓말이 된다 — 그 칸엔 결재 요청이
                             나가지 않으므로(getLineKindOptions 주석) 「통지가 나가지 않습니다」가
                             경고할 대상 자체가 없고, 미정 상태에선 연락처가 «없는» 게 아니라
                             «아직 정해지지 않은» 것이다. 증상(종류 0)이 같아 한 문구로 묶이기
                             쉽지만 원인과 조치가 다르다(I-1이 미해소 칸을 가른 것과 같은 축).
                             이 행의 안내는 아래 「이 칸은 위임 담당자로 채워집니다」가 맡는다. */
                          !isDelegationOwnedSlot(line) && (
                            <div className="px-3 mt-1 text-xs text-amber-600">
                              연락처가 없어 SMS·이메일 통지가 나가지 않습니다. 인앱 알림으로만
                              전달되며, 담당자가 로그인하지 않으면 결재 요청을 보지 못합니다.
                            </div>
                          )
                        )}
                        {/*
                          **담당자 교체** (2026-09-09) — 지정된 칸에서도 그 자리에서 사람을 바꾼다.
                          종전엔 이 갈래에 지정 수단이 없어 `담당자 해제` → 재지정 두 걸음을 거쳐야
                          했는데, 그 사이가 **미지정 칸**이다. 미지정은 이 시스템에서 의미 있는
                          상태(오프라인 종이 결재)이고 prefix 규칙의 판정 대상이라, 중간 칸을 바꾸려
                          하면 뒤 칸이 전부 "앞 칸이 미지정이라 지정할 수 없습니다"가 된다 —
                          사람 하나 바꾸려던 조작이 실수로 저장되면 뒤 칸이 종이로 강등된다.

                          ## locked 칸에서도 연다
                          `담당자 해제` 버튼이 이미 locked에서 활성이고 그 근거가 「locked는 칸
                          **구조**에 대한 권한이지 담당자에 대한 것이 아니다」(결정 2026-08-13 §7)다.
                          해제가 열려 있는데 교체만 막으면 **더 위험한 길만 열어 둔 셈**이 된다.

                          ## 이 갈래에만 붙인다
                          미해소 칸·`_parkedFill` 행은 바로 위 블록이 각자의 사유 문구와 함께 이미
                          select를 낸다(S66·park-and-resume). 여기서 또 내면 한 행에 select가 둘이 된다.
                          `parkedFillMode`에서는 비대기 칸이 **전 행 구조 잠금**이라 열지 않는다 —
                          서버가 같은 범위로 거부한다(`APPROVAL_LINE_PARKED_ONLY_EDIT`).
                        */}
                        {/* 위임이 소유한 칸은 열지 않는다 — 여기서 다른 사람을 고르면 저장 시
                            서버 해소가 이겨 「골랐는데 원래대로」가 된다. 그 값은 위임 탭이 정한다.
                            그래서 안내 한 줄로 **어디서 정하는지**를 말한다(막기만 하면 막다른 길). */}
                        {isDelegationOwnedSlot(line) ? (
                          <p className="px-3 mt-1 text-xs text-ink-400">
                            {/* 담당자와 채널을 한 문장으로 묶는다 — 둘 다 이 화면에서 뺐으므로
                                어디서 정하는지 한 곳에서 말해야 막다른 길이 되지 않는다. */}
                            {/* 「위」를 빼는 이유(G-14): 같은 모달이 **상세**에서도 열린다. 거기엔
                                그 섹션이 화면 위에 있지 않고 위임 모달 안에 있어, 「위」라 하면 없는
                                자리를 가리킨다. 바로 아래 버튼 title은 처음부터 이 형태였다. */}
                            이 칸의 담당자와 알림 채널은 「담당자에게 위임」에서 정합니다.
                          </p>
                        ) : (
                          !parkedFillMode &&
                          !isUnresolvedApprovalStep(line) &&
                          !isParkedFillLine(line) && (
                            <div className="px-3">
                              {renderAssigneeSelect(line, index, prefixBlocked, 'replace')}
                            </div>
                          )
                        )}
                        </>
                        )}
                      </div>

                      {/* 행 액션 — 원래 24px(xs) `ghost` 아이콘 4개가 2px 간격으로 붙어 있었다.
                          ⚠️ 가장 큰 문제는 크기가 아니라 **바탕이었다**: `ghost`는 배경도 테두리도 없어서
                          미지정 칸의 amber 행 위에서는 버튼이 있는지조차 보이지 않았고, 이 화면은 칸이
                          전부 미지정일 때 대부분의 버튼이 비활성이라 30% 불투명도까지 겹쳐 완전히 사라졌다.
                          → `secondary`(흰 배경 + 테두리)로 바꿔 **행 색과 무관하게** 버튼임이 보이게 한다.
                          투명·상속에 기대는 시각 값이 호스트 바탕에 따라 사라지는 전형(canvas-shell 계열).
                          크기는 32px(sm) 버튼 + 14px 아이콘(44%). 24px(xs)+16px에서 출발해 38px+28px까지
                          올려 봤다가 되돌린 값으로, 매 단계 DOM 실측으로 맞췄다. **순서 이동**(↑↓)과
                          **담당자/칸 조작**(해제·삭제)을 구분선으로 갈라 성격이 다른 두 무리임을
                          눈으로 알 수 있게 한다.
                          ⚠️ **`shrink-0`이 없으면 아이콘이 가로로만 찌그러진다.** svg는 width/height
                          속성 없이 viewBox만 있어 intrinsic 폭이 없고, Button 셸이 flex라
                          flex-shrink 기본값 1로 눌린다. 실측(DOM)에서 `w-7 h-7`(28×28)이 **8×28**로
                          렌더됐다 — 그래서 18→24→28px로 올려도 화면은 거의 그대로였다.
                          크기만 키우고 이걸 놓치면 "왜 안 커지지"로 시간을 버린다.
                          ⚠️ 아이콘 크기는 **글리프가 viewBox를 얼마나 채우는지**까지 봐야 한다 —
                          chevron은 24 viewBox 안에서 가로 58%·세로 29%만 차지해, 같은 px여도
                          휴지통·사람− 보다 작아 보인다.
                          해제(사람−)와 삭제(휴지통)는 결과가 크게 달라 가장 헷갈리던 짝이라 삭제만 색을 준다. */}
                      <div className="flex-shrink-0 flex items-center gap-1 px-2 border-l border-line self-stretch">
                        <Button
                          variant="secondary"
                          size="sm"
                          iconOnly
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMoveUp(index); }}
                          // locked 단계 자체 이동 불가, locked 단계 바로 아래 행도 위로 이동 차단 (swap 방지)
                          disabled={index === 0 || isLineLocked || prevLineLocked}
                          // 비활성도 "있다"는 건 보여야 한다 — 30%는 색 깔린 행에서 사실상 사라졌다.
                          // (Button 셸의 기본 disabled:opacity-50을 그대로 쓴다)
                          title="위로"
                        >
                          <ChevronUpIcon className="w-3.5 h-3.5 shrink-0" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          iconOnly
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMoveDown(index); }}
                          // locked 단계 자체 이동 불가
                          disabled={
                            index === editingLines.length - 1 ||
                            isLineLocked
                          }
                          // 비활성도 "있다"는 건 보여야 한다 — 30%는 색 깔린 행에서 사실상 사라졌다.
                          // (Button 셸의 기본 disabled:opacity-50을 그대로 쓴다)
                          title="아래로"
                        >
                          <ChevronDownIcon className="w-3.5 h-3.5 shrink-0" />
                        </Button>
                        <span className="self-stretch w-px bg-line mx-0.5" aria-hidden="true" />
                        {/* 담당자 해제 — 칸은 남기고 담당자만 뗀다. 삭제(칸 제거)와 별개 조작이라
                            아이콘·문구를 명확히 구분한다. locked 칸에서도 활성이다: locked는 칸
                            구조에 대한 권한이지 담당자에 대한 것이 아니다(결정 2026-08-13 §7). */}
                        <Button
                          variant="secondary"
                          size="sm"
                          iconOnly
                          // 대기 칸 지정 모드에서는 해제 불가 — "종이 결재로 강등"은 대기 칸
                          // 해소가 아니라 구조 변경이라 서버가 거부한다. 지정을 바꾸려면 select로
                          // 다시 고른다(_parkedFill 행은 지정 후에도 select가 남는다).
                          //
                          // 위임이 소유한 칸(F-48, 2026-09-13 사용자 「위임 탭에서 담당자를 지정하게
                          // 되어 있잖아」)도 해제 불가 — 그 칸의 담당자는 위 「담당자에게 위임」이
                          // 정한다. 교체 select는 이미 같은 이유로 닫혀 있었는데(아래 안내 문구)
                          // 해제만 열려 있어 **더 위험한 길만 열어 둔** 상태였다: 여기서 떼면 칸이
                          // 미지정(종이 결재)이 되어 담당자의 작성 완료 서명이 preset으로 앉을 자리
                          // (`sourceType: 'delegatedTo'`)를 잃는다.
                          disabled={lineUnassigned || parkedFillMode || isDelegationOwnedSlot(line)}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (lineUnassigned || parkedFillMode || isDelegationOwnedSlot(line)) return;
                            onUnsetAssignee(index);
                          }}
                          // 삭제(빨강)와 나란히 서므로 hover를 amber로 둔다 — 미지정 칸 배지와 같은 색이라
                          // "이 버튼을 누르면 저 상태가 된다"가 색으로 이어진다.
                          className={lineUnassigned ? '' : 'hover:bg-amber-50 hover:text-amber-700'}
                          title={
                            isDelegationOwnedSlot(line)
                              ? '이 칸의 담당자는 「담당자에게 위임」에서 정합니다'
                              : lineUnassigned
                                ? '이미 담당자가 지정되지 않은 칸입니다'
                                : '담당자 해제 (칸은 유지 — 종이로 결재)'
                          }
                          aria-label={
                            isDelegationOwnedSlot(line)
                              ? '이 칸의 담당자는 「담당자에게 위임」에서 정합니다'
                              : lineUnassigned
                                ? '이미 담당자가 지정되지 않은 칸입니다'
                                : '담당자 해제 (칸은 유지 — 종이로 결재)'
                          }
                        >
                          <UserMinusIcon className="w-3.5 h-3.5 shrink-0" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          iconOnly
                          disabled={isLineLocked}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isLineLocked) return;
                            if (window.confirm(
                              lineUnassigned
                                ? `'${getStepLabel(index)}' 결재란을 삭제하시겠습니까? (담당자 해제가 아니라 칸 자체가 없어집니다)`
                                : `${getLineName(line)}님을 결재라인에서 삭제하시겠습니까?`,
                            )) {
                              onRemoveApprover(index);
                            }
                          }}
                          // 파괴적 액션이라 hover만 rose로 덮는다(형제 이동 버튼과 같은 ghost 서열 유지).
                          // locked(disabled)일 때는 hover 유틸이 disabled 버튼에도 적용되므로 아예 붙이지 않는다.
                          // 파괴적 액션이라 **평소에도** rose로 둔다 — 바로 옆 '담당자 해제'와 결과가
                          // 크게 다른데(칸 제거 vs 담당자만 해제), hover에만 색을 주면 마우스를 올리기
                          // 전까지 둘이 같은 회색 아이콘이라 구분이 안 됐다.
                          className={isLineLocked ? '' : 'text-rose-500 hover:bg-rose-50 hover:text-rose-600'}
                          title={isLineLocked ? '고정 단계는 삭제할 수 없습니다' : '결재란 삭제 (칸 자체를 없앰)'}
                          aria-label={isLineLocked ? '고정 단계는 삭제할 수 없습니다' : '결재란 삭제 (칸 자체를 없앰)'}
                        >
                          <TrashIcon className="w-3.5 h-3.5 shrink-0" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* 대기 칸 지정 모드에서는 칸 추가 수단을 통째로 숨긴다 — 단계 추가는 서버가
                  거부하는 구조 변경이다(APPROVAL_LINE_PARKED_ONLY_EDIT). */}
              {parkedFillMode ? null : isAtMaxApprovers ? (
                <div className="py-2 px-3 text-sm text-ink-400 border border-dashed border-ink-200 rounded-card text-center">
                  {/* 안내 숫자는 admin의 추가 한도(maxAdditionalLines)를 그대로 쓴다 —
                      양식이 정의한 칸은 한도를 소비하지 않으므로 이 값이 곧 "더 만들 수 있는 수"다.
                      maxApprovers(전체 칸 상한) 기준 계산은 틀리므로 쓰지 않는다. */}
                  {maxReachedMessage}
                </div>
              ) : (
                <div>
                  {/* 주 액션은 **결재란 추가**다 — 칸을 만드는 것과 담당자를 정하는 것은 별개 축이고
                      (결정 2026-08-13), 담당자 지정은 각 칸의 '담당자 지정...'에서 한다.
                      종전에는 이 자리가 '결재자 추가'(멤버를 고르면 칸이 생김)뿐이라 **칸만 늘릴
                      수단이 없었고**, 미지정 칸이 하나라도 있으면 prefix 가드에 걸려 추가 자체가
                      막혔다. 빈 칸은 목록 끝에 붙어 prefix를 어기지 않으므로 그 가드가 필요 없다. */}
                  <button
                    type="button"
                    onClick={onAddUnassignedSlot}
                    className="w-full py-2.5 px-3 text-sm border border-dashed border-ink-300 rounded-control text-ink-500 bg-white hover:border-ink-400 hover:text-ink-700 transition-colors cursor-pointer"
                  >
                    + 결재란 추가
                  </button>
                  <p className="mt-1 text-xs text-ink-400">
                    빈 칸으로 추가됩니다. 담당자는 각 칸의 <strong>담당자 지정</strong>에서 정하세요
                    (비워 두면 종이로 결재).
                  </p>
                  {/* 현장·업체 담당자 신규 등록은 행 단위 지정 목록에 없는 기능이라 여기 남긴다.
                      이 경로는 **담당자가 지정된 칸**을 끝에 만들므로 prefix 가드를 그대로 받는다. */}
                  <button
                    type="button"
                    disabled={hasUnassignedSlot}
                    onClick={() => onAddDropdownChange('__new__')}
                    className="mt-2 text-xs text-brand-600 hover:text-brand-700 underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    + 현장·업체 담당자 추가
                  </button>
                  {hasUnassignedSlot && (
                    <p className="mt-1 text-xs text-amber-700">
                      담당자 미지정 칸이 있어 새 담당자를 바로 등록할 수 없습니다. 담당자는 앞 칸부터
                      차례로 지정해야 합니다 — 칸만 더 필요하면 위 <strong>결재란 추가</strong>를 쓰세요.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {errorMessage && (
            <p className="text-sm text-red-600 mt-3">{errorMessage}</p>
          )}

          {showSaveAsSiteDefault && (
            <div className="mt-3 pt-3 border-t border-line-soft">
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={saveAsSiteDefault}
                  onChange={(e) => onToggleSaveAsSiteDefault?.(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 flex-shrink-0"
                />
                <div>
                  <span className="text-sm text-ink-700 font-medium">이 현장 기본 결재선으로 저장</span>
                  <p className="text-xs text-ink-400 mt-0.5">
                    체크하면 이 현장의 같은 보고서 유형 기본 결재선이 갱신됩니다. (추가 결재 최대 수는 관리자 설정 유지)
                  </p>
                </div>
              </label>
            </div>
          )}

    </Modal>
  );
}
